import styles from "./NeedHelpScreen.module.scss";

import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";

import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { AppButton } from "@/modules/shared/components/AppButton";
import { AppInput } from "@/modules/shared/components/AppInput";
import { PageContainer } from "@/modules/shared/components/PageContainer";
import { ROUTES } from "@/modules/shared/consts/routes";
import { ArrowIcon } from "@/modules/shared/svgs/ArrowIcon";
import { useSupportApi } from "@/modules/support/hooks/useSupportApi";
import {
  createSupportFormSchema,
  MAX_QUESTION_LENGTH,
} from "@/modules/support/schemas/supportFormSchema";

type FormErrors = {
  email?: string;
  question?: string;
};

export const NeedHelpScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();
  const isEmbedRoute = location.pathname.startsWith("/embed");

  const { requestSupport, requestSupportMutation } = useSupportApi({
    onSuccess: () => {
      const confirmationPath = isEmbedRoute
        ? ROUTES.support.embed.confirmation
        : ROUTES.support.confirmation;
      navigate({ to: confirmationPath });
    },
    onError: (error) => {
      console.error("Support request failed:", error);
    },
  });

  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  const schema = useMemo(() => createSupportFormSchema(t), [t]);

  const validateForm = (): boolean => {
    const result = schema.safeParse({
      email: email.trim(),
      question: question.trim(),
    });

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setErrors({
        email: fieldErrors.email?.[0],
        question: fieldErrors.question?.[0],
      });
      return false;
    }

    setErrors({});
    return true;
  };

  const handleEmailChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleQuestionChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setQuestion(value);

    if (errors.question && value.length <= MAX_QUESTION_LENGTH) {
      setErrors((prev) => ({ ...prev, question: undefined }));
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    requestSupport({
      email: email.trim(),
      question: question.trim(),
    });
  };

  const handleGoBack = useCallback(() => {
    router.history.back();
  }, [router]);

  const isQuestionExceeded = question.length > MAX_QUESTION_LENGTH;
  const exceededText = t("needHelp.errors.characterLimitExceeded");

  const characterCounterFormat = (count: number, max: number) => {
    const baseText = `${count} of ${max}`;
    return count > max ? `${baseText} ${exceededText}` : baseText;
  };

  return (
    <PageContainer variant="fullWidth">
      <div className={isEmbedRoute ? styles.wrapperEmbed : styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.copySection}>
            <div className={styles.titleRow}>
              {router.history.canGoBack() && (
                <button
                  className={styles.backButton}
                  onClick={handleGoBack}
                  type="button"
                  aria-label={t("needHelp.backButton")}
                >
                  <ArrowIcon />
                </button>
              )}
              <h1 className={styles.title}>{t("needHelp.title")}</h1>
            </div>
            <p className={styles.description}>{t("needHelp.description")}</p>
          </div>

          <div className={styles.divider} />

          <form className={styles.formSection} onSubmit={handleSubmit}>
            <AppInput
              id="contact-email"
              type="email"
              label={t("needHelp.form.emailLabel")}
              placeholder={t("needHelp.form.emailPlaceholder")}
              value={email}
              onChange={handleEmailChange}
              error={errors.email}
              disabled={requestSupportMutation.isPending}
              dataAttributes={{ "data-1p-ignore": "true" }}
            />

            <AppInput
              id="question"
              type="textarea"
              label={t("needHelp.form.questionLabel")}
              placeholder={t("needHelp.form.questionPlaceholder")}
              value={question}
              onChange={handleQuestionChange}
              error={
                isQuestionExceeded
                  ? exceededText
                  : errors.question && !isQuestionExceeded
                    ? errors.question
                    : undefined
              }
              maxLength={MAX_QUESTION_LENGTH}
              showCharacterCounter={true}
              characterCounterFormat={characterCounterFormat}
              autoGrow={true}
              enforceMaxLength={false}
              disabled={requestSupportMutation.isPending}
            />

            <AppButton
              type="submit"
              variant="kendoPrimary"
              disabled={requestSupportMutation.isPending}
              isLoading={requestSupportMutation.isPending}
            >
              {t("needHelp.form.sendButton")}
            </AppButton>
          </form>
        </div>
      </div>
    </PageContainer>
  );
};
